import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    FormControlLabel,
    Checkbox,
    Alert,
    Card,
    CardContent,
    Divider,
    Stack,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress
} from '@mui/material';
import {
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    Schedule as ScheduleIcon,
    FilterList as FilterIcon,
    ExpandMore as ExpandMoreIcon,
    AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

export default function EditBroadcast() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [task, setTask] = useState(null);

    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [selectedBots, setSelectedBots] = useState([]);
    const [bots, setBots] = useState([]);
    const [priority, setPriority] = useState(0);
    const [scheduledAt, setScheduledAt] = useState(null);
    const [useSchedule, setUseSchedule] = useState(false);

    const [filters, setFilters] = useState({
        userStatus: 'all',
        minBalance: 0,
        maxBalance: null,
        hasOrders: null,
        lastActivityDays: null
    });

    const [sendingSettings, setSendingSettings] = useState({
        delayBetweenMessages: 100,
        delayBetweenBots: 0,
        sendOnlyInTimeRange: {
            enabled: false,
            startTime: '09:00',
            endTime: '21:00'
        }
    });

    useEffect(() => {
        loadBots();
        loadTask();
    }, [id]);

    const loadBots = async () => {
        try {
            const response = await axios.get('/telegram-bots');
            setBots(response.data);
        } catch (error) {
            enqueueSnackbar('Ошибка загрузки ботов', { variant: 'error' });
        }
    };

    const loadTask = async () => {
        try {
            const response = await axios.get(`/broadcasts/${id}`);
            const taskData = response.data;
            
            setTask(taskData);
            setTitle(taskData.title);
            setMessage(taskData.message);
            setSelectedBots(taskData.bots.map(b => b._id));
            setPriority(taskData.priority);
            setFilters(taskData.filters);
            
            if (taskData.scheduledAt) {
                setScheduledAt(new Date(taskData.scheduledAt));
                setUseSchedule(true);
            }

            if (taskData.sendingSettings) {
                setSendingSettings(taskData.sendingSettings);
            }
        } catch (error) {
            enqueueSnackbar('Ошибка загрузки рассылки', { variant: 'error' });
            navigate('/broadcasts');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title.trim()) {
            enqueueSnackbar('Введите название рассылки', { variant: 'warning' });
            return;
        }

        if (!message.trim()) {
            enqueueSnackbar('Введите текст сообщения', { variant: 'warning' });
            return;
        }

        if (selectedBots.length === 0) {
            enqueueSnackbar('Выберите хотя бы одного бота', { variant: 'warning' });
            return;
        }

        try {
            setSaving(true);

            const data = {
                title,
                message,
                bots: selectedBots,
                filters,
                priority,
                scheduledAt: useSchedule ? scheduledAt : null,
                sendingSettings
            };

            await axios.put(`/broadcasts/${id}`, data);

            enqueueSnackbar('Рассылка обновлена успешно', { variant: 'success' });
            navigate('/broadcasts');
        } catch (error) {
            console.error('Error updating broadcast:', error);
            enqueueSnackbar(error.response?.data?.message || 'Ошибка обновления рассылки', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSendingSettingChange = (field, value) => {
        setSendingSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleTimeRangeChange = (field, value) => {
        setSendingSettings(prev => ({
            ...prev,
            sendOnlyInTimeRange: {
                ...prev.sendOnlyInTimeRange,
                [field]: value
            }
        }));
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (task?.status === 'in_progress') {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="warning">
                    Эта рассылка выполняется и не может быть отредактирована.
                </Alert>
                <Button onClick={() => navigate('/broadcasts')} sx={{ mt: 2 }}>
                    Вернуться к списку
                </Button>
            </Container>
        );
    }


    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigate('/broadcasts')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1">
                    Редактировать рассылку
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Stack spacing={3}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Основная информация
                                </Typography>
                                <Divider sx={{ mb: 3 }} />

                                <Stack spacing={3}>
                                    <TextField
                                        label="Название рассылки"
                                        fullWidth
                                        required
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                    />

                                    <TextField
                                        label="Текст сообщения"
                                        fullWidth
                                        required
                                        multiline
                                        rows={8}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        helperText={`Символов: ${message.length} / 4096`}
                                    />

                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Выберите ботов *
                                            </Typography>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    if (selectedBots.length === bots.length) {
                                                        setSelectedBots([]);
                                                    } else {
                                                        setSelectedBots(bots.map(b => b._id));
                                                    }
                                                }}
                                            >
                                                {selectedBots.length === bots.length ? 'Снять все' : 'Выбрать всех'}
                                            </Button>
                                        </Box>
                                        <FormControl fullWidth required>
                                            <Select
                                                multiple
                                                value={selectedBots}
                                                onChange={(e) => setSelectedBots(e.target.value)}
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (selected.length === 0) {
                                                        return <em>Не выбрано</em>;
                                                    }
                                                    return (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => {
                                                                const bot = bots.find(b => b._id === value);
                                                                return (
                                                                    <Chip
                                                                        key={value}
                                                                        label={bot?.username || value}
                                                                        size="small"
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    );
                                                }}
                                            >
                                                {bots.map((bot) => (
                                                    <MenuItem key={bot._id} value={bot._id}>
                                                        @{bot.username}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Stack>
                            </Paper>

                            <Paper sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <FilterIcon color="primary" />
                                    <Typography variant="h6">
                                        Фильтры получателей
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 3 }} />

                                <Stack spacing={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Статус пользователя</InputLabel>
                                        <Select
                                            value={filters.userStatus}
                                            onChange={(e) => handleFilterChange('userStatus', e.target.value)}
                                            label="Статус пользователя"
                                        >
                                            <MenuItem value="all">Все пользователи</MenuItem>
                                            <MenuItem value="active">Только активные</MenuItem>
                                            <MenuItem value="blocked">Только заблокированные</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Минимальный баланс"
                                                type="number"
                                                fullWidth
                                                value={filters.minBalance}
                                                onChange={(e) => handleFilterChange('minBalance', Number(e.target.value))}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Максимальный баланс"
                                                type="number"
                                                fullWidth
                                                value={filters.maxBalance || ''}
                                                onChange={(e) => handleFilterChange('maxBalance', e.target.value ? Number(e.target.value) : null)}
                                            />
                                        </Grid>
                                    </Grid>

                                    <FormControl fullWidth>
                                        <InputLabel>Наличие заказов</InputLabel>
                                        <Select
                                            value={filters.hasOrders === null ? 'all' : filters.hasOrders ? 'yes' : 'no'}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                handleFilterChange('hasOrders', val === 'all' ? null : val === 'yes');
                                            }}
                                            label="Наличие заказов"
                                        >
                                            <MenuItem value="all">Не важно</MenuItem>
                                            <MenuItem value="yes">Есть заказы</MenuItem>
                                            <MenuItem value="no">Нет заказов</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        label="Активность за последние дни"
                                        type="number"
                                        fullWidth
                                        value={filters.lastActivityDays || ''}
                                        onChange={(e) => handleFilterChange('lastActivityDays', e.target.value ? Number(e.target.value) : null)}
                                    />
                                </Stack>
                            </Paper>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AccessTimeIcon color="primary" />
                                        <Typography variant="h6">
                                            Настройки времени отправки
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={3}>
                                        <TextField
                                            label="Задержка между сообщениями (мс)"
                                            type="number"
                                            fullWidth
                                            value={sendingSettings.delayBetweenMessages}
                                            onChange={(e) => handleSendingSettingChange('delayBetweenMessages', Number(e.target.value))}
                                            helperText="Рекомендуется 100-500 мс для избежания блокировок"
                                        />

                                        <TextField
                                            label="Задержка между ботами (мс)"
                                            type="number"
                                            fullWidth
                                            value={sendingSettings.delayBetweenBots}
                                            onChange={(e) => handleSendingSettingChange('delayBetweenBots', Number(e.target.value))}
                                            helperText="Задержка после завершения отправки через один бот перед следующим"
                                        />

                                        <Divider />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={sendingSettings.sendOnlyInTimeRange.enabled}
                                                    onChange={(e) => handleTimeRangeChange('enabled', e.target.checked)}
                                                />
                                            }
                                            label="Отправлять только в определенное время"
                                        />

                                        {sendingSettings.sendOnlyInTimeRange.enabled && (
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        label="Начало (HH:MM)"
                                                        type="time"
                                                        fullWidth
                                                        value={sendingSettings.sendOnlyInTimeRange.startTime}
                                                        onChange={(e) => handleTimeRangeChange('startTime', e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        label="Конец (HH:MM)"
                                                        type="time"
                                                        fullWidth
                                                        value={sendingSettings.sendOnlyInTimeRange.endTime}
                                                        onChange={(e) => handleTimeRangeChange('endTime', e.target.value)}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        )}

                                        {sendingSettings.sendOnlyInTimeRange.enabled && (
                                            <Alert severity="info">
                                                Если текущее время вне диапазона, рассылка будет приостановлена на 1 минуту и проверена снова
                                            </Alert>
                                        )}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Stack spacing={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Настройки отправки
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />

                                    <Stack spacing={2}>
                                        <TextField
                                            label="Приоритет"
                                            type="number"
                                            fullWidth
                                            value={priority}
                                            onChange={(e) => setPriority(Number(e.target.value))}
                                            helperText="Чем выше число, тем выше приоритет"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={useSchedule}
                                                    onChange={(e) => setUseSchedule(e.target.checked)}
                                                />
                                            }
                                            label="Запланировать отправку"
                                        />

                                        {useSchedule && (
                                            <DateTimePicker
                                                label="Дата и время отправки"
                                                value={scheduledAt}
                                                onChange={setScheduledAt}
                                                renderInput={(params) => <TextField {...params} fullWidth />}
                                                minDateTime={new Date()}
                                            />
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={saving}
                                startIcon={<SendIcon />}
                            >
                                {saving ? 'Сохранение...' : 'Сохранить изменения'}
                            </Button>

                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => navigate('/broadcasts')}
                                disabled={saving}
                            >
                                Отмена
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
}
